import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { getArticles } from "@/lib/api";
import type { BlogArticle } from "@/lib/types";

function ArticleSkeleton({ featured = false }: { featured?: boolean }) {
  if (featured) {
    return (
      <div className="mb-12 flex flex-col lg:flex-row bg-[#e0f7fa]/30 rounded-3xl overflow-hidden animate-pulse">
        <div className="lg:w-1/2 h-64 lg:h-80 bg-[#b2ebf2]"></div>
        <div className="lg:w-1/2 p-8 md:p-12 flex flex-col justify-center gap-4">
          <div className="h-3 bg-[#b2ebf2] rounded-full w-24"></div>
          <div className="h-7 bg-[#b2ebf2] rounded-full w-3/4"></div>
          <div className="h-5 bg-[#b2ebf2] rounded-full w-full"></div>
          <div className="h-5 bg-[#b2ebf2] rounded-full w-5/6"></div>
          <div className="flex gap-4 mt-2">
            <div className="h-3 bg-[#b2ebf2] rounded-full w-20"></div>
            <div className="h-3 bg-[#b2ebf2] rounded-full w-20"></div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-[#fff5f8] rounded-2xl overflow-hidden animate-pulse">
      <div className="h-48 bg-[#f9d0dc]"></div>
      <div className="p-5 space-y-3">
        <div className="h-4 bg-[#f9d0dc] rounded-full w-3/4"></div>
        <div className="h-4 bg-[#f9d0dc] rounded-full w-full"></div>
        <div className="h-3 bg-[#f9d0dc] rounded-full w-2/3"></div>
        <div className="flex justify-between mt-2">
          <div className="h-3 bg-[#f9d0dc] rounded-full w-24"></div>
          <div className="h-3 bg-[#f9d0dc] rounded-full w-12"></div>
        </div>
      </div>
    </div>
  );
}

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState("Todos");
  const [search, setSearch] = useState("");
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadArticles = async () => {
      try {
        setLoading(true);

        const data = await getArticles();

        const sorted = [...data].sort(
          (a, b) =>
            new Date(b.created_at).getTime() -
            new Date(a.created_at).getTime()
        );

        setArticles(sorted);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadArticles();
  }, []);

  const categories = useMemo(() => {
    const counts: Record<string, number> = { Todos: articles.length };
    articles.forEach((a) => {
      counts[a.category] = (counts[a.category] || 0) + 1;
    });
    return Object.entries(counts).map(([name, count]) => ({ name, count }));
  }, [articles]);

  const filtered = useMemo(() => {
    return articles.filter((a) => {
      const matchCat = activeCategory === "Todos" || a.category === activeCategory;
      const matchSearch =
        search === "" ||
        a.title.toLowerCase().includes(search.toLowerCase()) ||
        a.excerpt.toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    });
  }, [articles, activeCategory, search]);

  const featured = filtered.find((a) => a.featured) || filtered[0];
  const rest = filtered.filter((a) => a.id !== featured?.id);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative h-72 md:h-96 flex items-end pb-12">
        <img
          src="https://readdy.ai/api/search-image?query=stunning%20decorated%20celebration%20cakes%20with%20colorful%20buttercream%20roses%20swirls%20and%20edible%20flowers%2C%20elegant%20pasteleria%20display%2C%20pink%20cream%20and%20gold%20tones%2C%20artistic%20cake%20decoration%20close%20up%2C%20professional%20confectionery%20photography%2C%20dessert%20boutique%20aesthetic%2C%20soft%20dreamy%20lighting%2C%20luxurious%20pastry%20art&width=1440&height=500&seq=blog-hero-pasteleria&orientation=landscape"
          alt="Blog"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full pt-20">
          <span className="text-[#7ee8f4] text-xs font-semibold uppercase tracking-widest">
            Inicio / Blog
          </span>
          <h1
            className="text-4xl md:text-5xl font-bold text-white mt-2"
          >
            Blog San7o Pecado
          </h1>
          <p className="text-white/75 mt-2 text-base">
            Recetas, consejos, historias y tendencias del mundo dulce.
          </p>
        </div>
      </section>

      {/* Search + Categories */}
      <section className="sticky top-20 z-30 bg-white border-b border-[#f5e6d3] py-4 px-4 md:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {loading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-8 w-20 bg-[#f9d0dc] rounded-full animate-pulse"></div>
                ))
              : categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => setActiveCategory(cat.name)}
                    className={`text-xs font-semibold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                      activeCategory === cat.name
                        ? "bg-[#00b4c8] text-white"
                        : "bg-[#e0f7fa]/60 text-[#6b5b6e] hover:bg-[#e0f7fa]"
                    }`}
                  >
                    {cat.name}
                    <span className="ml-1.5 opacity-60">({cat.count})</span>
                  </button>
                ))}
          </div>
          <div className="relative w-full sm:w-56">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-[#b09080] text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar artículo..."
              className="w-full pl-9 pr-4 py-2 rounded-full border border-[#b2ebf2] text-sm text-[#2d1a0e] placeholder-[#9b8aae] focus:outline-none focus:border-[#00b4c8] bg-[#e0f7fa]/30"
            />
          </div>
        </div>
      </section>

      <section className="py-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <>
              <ArticleSkeleton featured />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 3 }).map((_, i) => (
                  <ArticleSkeleton key={i} />
                ))}
              </div>
            </>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-article-line text-5xl text-[#00b4c8]/40"></i>
              <p className="text-[#6b5b6e] mt-4">No encontramos artículos con esa búsqueda.</p>
            </div>
          ) : (
            <>
              {/* Featured Article */}
              {featured && (
                <div className="mb-12">
                  <Link to={`/blog/${featured.id}`} className="group flex flex-col lg:flex-row bg-[#e0f7fa]/30 rounded-3xl overflow-hidden hover:shadow-sm transition-all cursor-pointer">
                    <div className="lg:w-1/2 relative overflow-hidden">
                      <img
                        src={featured.image}
                        alt={featured.title}
                        className="w-full h-64 lg:h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <span className="absolute top-4 left-4 bg-[#00b4c8] text-white text-xs font-bold px-3 py-1 rounded-full">
                        Destacado
                      </span>
                    </div>
                    <div className="lg:w-1/2 p-8 md:p-12 flex flex-col justify-center">
                      <span className="text-xs font-semibold text-[#00b4c8] uppercase tracking-wider mb-3">
                        {featured.category}
                      </span>
                      <h2
                        className="text-2xl md:text-3xl font-bold text-[#2d1a0e] mb-4 leading-tight"
                      >
                        {featured.title}
                      </h2>
                      <p className="text-[#6b5b6e] text-sm leading-relaxed mb-6">
                        {featured.excerpt}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-[#b09080]">
                        <span className="flex items-center gap-1">
                          <i className="ri-user-line"></i> {featured.author}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line"></i> {featured.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i> {featured.read_time}
                        </span>
                      </div>
                      <div className="mt-6">
                        <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#00b4c8] group-hover:gap-2 transition-all">
                          Leer Artículo <i className="ri-arrow-right-line"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                </div>
              )}

              {/* Rest of Articles */}
              {rest.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {rest.map((article) => (
                    <Link
                      key={article.id}
                      to={`/blog/${article.id}`}
                      className="group bg-[#e0f7fa]/20 rounded-2xl overflow-hidden hover:shadow-sm transition-all cursor-pointer block"
                    >
                      <div className="relative overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-48 object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                        <span className="absolute top-3 left-3 bg-white/90 text-[#00b4c8] text-xs font-bold px-2.5 py-1 rounded-full">
                          {article.category}
                        </span>
                      </div>
                      <div className="p-5">
                        <h3
                          className="font-bold text-[#2d1a0e] text-base mb-2 leading-tight line-clamp-2"
                        >
                          {article.title}
                        </h3>
                        <p className="text-[#6b5b6e] text-xs leading-relaxed mb-4 line-clamp-2">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-[#b09080]">
                          <div className="flex items-center gap-3">
                            <span className="flex items-center gap-1">
                              <i className="ri-calendar-line"></i> {article.date}
                            </span>
                            <span className="flex items-center gap-1">
                              <i className="ri-time-line"></i> {article.read_time}
                            </span>
                          </div>
                          <span className="text-[#00b4c8] font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                            Leer <i className="ri-arrow-right-line"></i>
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
